import React, { ReactNode, useContext, useEffect } from "react";
import { useRouter } from "next/router";

import Loader from "@vahak/core/dist/components/loader";
import { AppContext } from "@vahak/core/dist/app-context";
import { replaceLocation, shouldUseFullPageNavigation } from "@vahak/core-ui/dist/methods/browser";

import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import {
    getLoggedInDeepLinkHref,
    isAuthRequiredDeepLink
} from "../../../constants/deep-links";
import { useCompactLoadPostingOptional } from "../../homepage-v2/compact-load-posting/CompactLoadPostingProvider";
import { useCompactLoadBiddingOptional } from "../../homepage-v2/compact-load-bidding/CompactLoadBiddingProvider";

interface HomepageV2AuthGuardProps {
    children: ReactNode;
}

const HomepageV2AuthGuard = ({ children }: HomepageV2AuthGuardProps) => {
    const { isPhoneVerified, companyId } = useContext(AppContext);
    const router = useRouter();
    const compactPosting = useCompactLoadPostingOptional();
    const compactBidding = useCompactLoadBiddingOptional();

    const isLoggedIn = isPhoneVerified && !!companyId;
    const isOnHomepage = router.pathname === "/";
    const isCompactFlowOpen = Boolean(
        compactPosting?.isCompactPostingOpen ||
            compactPosting?.isCompactPostingOpening ||
            compactBidding?.isCompactBiddingOpen
    );
    const isAuthDeepLink = isAuthRequiredDeepLink(router.query, router.asPath);
    const shouldRedirect = isLoggedIn && isOnHomepage && (isAuthDeepLink || !isCompactFlowOpen);

    useEffect(() => {
        if (!shouldRedirect || !router.isReady) {
            return;
        }

        const redirectUrl = getLoggedInDeepLinkHref(router.query, router.asPath) ?? ROUTES.dashboard.getUrl();

        // iOS Safari cancels in-flight chunk/CSS/API fetches during client-side route changes.
        if (shouldUseFullPageNavigation()) {
            replaceLocation(redirectUrl);
            return;
        }

        router.replace(redirectUrl);
    }, [shouldRedirect, router.isReady, router.query, router.asPath, router]);

    if (shouldRedirect) {
        return <Loader />;
    }

    return <>{children}</>;
};

export default HomepageV2AuthGuard;
